import {
  type Contenido,
  esCodigoValido,
  esSeccionAutomatica,
  nuevoIdDeBloque,
  validarContenido,
} from "@gestiondoc/shared";
import { Extension, type JSONContent, mergeAttributes, Node } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import Heading from "@tiptap/extension-heading";
import Italic from "@tiptap/extension-italic";
import ListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import Text from "@tiptap/extension-text";
import Underline from "@tiptap/extension-underline";
import { Dropcursor, Gapcursor, UndoRedo } from "@tiptap/extensions";
import type { Node as NodoPM } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { MarcasDeRevision } from "./marcas.ts";
import {
  VistaFlujograma,
  VistaImagen,
  VistaListaDeVerificacion,
  VistaNota,
  VistaPaso,
  VistaPasos,
  VistaSeccion,
} from "./vistas.tsx";

/**
 * El esquema del editor (fase 3, paso 4): exactamente los nodos que `esquemaContenido` admite,
 * ni uno más. Lo que TipTap produce con estas extensiones es lo que se guarda, sin traducir
 * (decisión del paso 1); por eso los nombres y los atributos coinciden uno a uno con `shared`.
 *
 * Los nodos estándar se ajustan a lo que el esquema permite: celdas y pasos solo con párrafos,
 * títulos de nivel 2 y 3 (el 1 es el de la sección), listas sin anidar.
 */

/** Los tipos que llevan `attrs.id` (ADR-07): todos menos `doc` y `text`. */
export const TIPOS_CON_ID = [
  "seccion",
  "paragraph",
  "heading",
  "bulletList",
  "listItem",
  "pasos",
  "paso",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
  "imagen",
  "flujograma",
  "nota",
  "referencia",
  "listaDeVerificacion",
] as const;

/**
 * El documento: secciones, la orientación de la hoja (null = vertical, como siempre) y la
 * densidad de la letra (null = normal; "compacta" para los formatos de una sola hoja).
 */
const Documento = Document.extend({
  content: "seccion+",
  addAttributes() {
    return { orientacion: { default: null }, densidad: { default: null } };
  },
});

/**
 * La sección numerada de la hoja. No se selecciona, no se arrastra y no se borra: la
 * plantilla del tipo dice cuáles hay (ver `filterTransaction` en `BloqueId`). `isolating`
 * hace que Backspace al inicio no la funda con la anterior.
 */
const Seccion = Node.create({
  name: "seccion",
  group: "seccion",
  content: "block*",
  isolating: true,
  defining: true,
  selectable: false,
  draggable: false,
  addAttributes() {
    return {
      titulo: { default: "" },
      clave: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "section[data-seccion]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes, { "data-seccion": "" }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaSeccion);
  },
});

/** Lista de pasos con responsable: el bloque que distingue a un instructivo. */
const Pasos = Node.create({
  name: "pasos",
  group: "block",
  content: "paso+",
  isolating: true,
  defining: true,
  parseHTML() {
    return [{ tag: "div[data-pasos]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-pasos": "" }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaPasos);
  },
});

const Paso = Node.create({
  name: "paso",
  content: "paragraph+",
  isolating: true,
  defining: true,
  addAttributes() {
    return { responsable: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "div[data-paso]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-paso": "" }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaPaso);
  },
});

const Nota = Node.create({
  name: "nota",
  group: "block",
  content: "paragraph+",
  isolating: true,
  defining: true,
  addAttributes() {
    return { tono: { default: "nota" } };
  },
  parseHTML() {
    return [{ tag: "div[data-nota]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-nota": "" }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaNota);
  },
});

/** Una imagen ya subida a la API (`contenido.subirImagen`); acá solo va su id. */
const Imagen = Node.create({
  name: "imagen",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      archivoId: { default: "" },
      alt: { default: "" },
      ancho: { default: 100 },
    };
  },
  parseHTML() {
    return [{ tag: "figure[data-imagen]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["figure", mergeAttributes(HTMLAttributes, { "data-imagen": "" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaImagen);
  },
});

/** Código Mermaid; el SVG lo dibuja el paso 5 con la misma librería en pantalla y en el PDF. */
const Flujograma = Node.create({
  name: "flujograma",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { codigo: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "div[data-flujograma]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-flujograma": "" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaFlujograma);
  },
});

/** Fase 3b: la lista de verificación de un formato (áreas y puntos); se llena por hoja. */
const ListaDeVerificacion = Node.create({
  name: "listaDeVerificacion",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { areas: { default: [{ nombre: "Área", puntos: ["Punto a revisar"] }] } };
  },
  parseHTML() {
    return [{ tag: "div[data-lista-verificacion]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-lista-verificacion": "" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VistaListaDeVerificacion);
  },
});

/** Cita a otro documento controlado, en línea: (GES-REG-019). */
const Referencia = Node.create({
  name: "referencia",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return { codigo: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "span[data-referencia]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { "data-referencia": "", class: "ed-referencia" }),
      String(node.attrs.codigo),
    ];
  },
});

type OpcionesBloqueId = { tipoCodigo: string };

const claveBloqueId = new PluginKey("bloqueId");

/**
 * ADR-07: todo bloque lleva un id estable. Este plugin lo asigna al nacer un nodo (Enter, pegar,
 * insertar) y repara los repetidos (pegar un bloque copiado dentro del mismo documento). Además
 * cuida dos cosas que el esquema de ProseMirror no puede: que una sección sin bloques reciba
 * un párrafo para poder escribir, y que las secciones sean las de la plantilla, siempre —
 * cualquier transacción que las quite, agregue o reordene se descarta.
 */
const BloqueId = Extension.create<OpcionesBloqueId>({
  name: "bloqueId",
  addOptions() {
    return { tipoCodigo: "" };
  },
  addGlobalAttributes() {
    return [
      {
        types: [...TIPOS_CON_ID],
        attributes: {
          id: {
            default: null,
            // Enter parte el párrafo: el nuevo nace sin id y el plugin le da uno propio.
            keepOnSplit: false,
            parseHTML: (el) => el.getAttribute("data-id"),
            renderHTML: (attrs) => (attrs.id ? { "data-id": attrs.id } : {}),
          },
        },
      },
    ];
  },
  addProseMirrorPlugins() {
    const tipoCodigo = this.options.tipoCodigo;
    return [
      new Plugin({
        key: claveBloqueId,
        filterTransaction(tr, estado) {
          if (!tr.docChanged) return true;
          return clavesDeSecciones(tr.doc) === clavesDeSecciones(estado.doc);
        },
        appendTransaction(transacciones, _antes, estado) {
          if (!transacciones.some((t) => t.docChanged)) return null;
          return repararIds(estado.tr, estado.doc, tipoCodigo);
        },
      }),
    ];
  },
});

function clavesDeSecciones(doc: NodoPM): string {
  const claves: string[] = [];
  doc.forEach((n) => {
    claves.push(`${n.attrs.clave ?? ""}|${n.attrs.titulo ?? ""}`);
  });
  return claves.join("\n");
}

/** Ids faltantes o repetidos, y párrafo en las secciones editables vacías. Null si no hay nada. */
function repararIds(tr: Transaction, doc: NodoPM, tipoCodigo: string): Transaction | null {
  const vistos = new Set<string>();
  const conId = new Set<string>(TIPOS_CON_ID);
  let cambio = false;
  const vacias: number[] = [];
  doc.descendants((nodo, pos) => {
    if (!conId.has(nodo.type.name)) return;
    const id = nodo.attrs.id;
    if (typeof id !== "string" || vistos.has(id)) {
      tr.setNodeMarkup(pos, undefined, { ...nodo.attrs, id: nuevoIdDeBloque() }, nodo.marks);
      cambio = true;
    } else {
      vistos.add(id);
    }
    if (
      nodo.type.name === "seccion" &&
      nodo.childCount === 0 &&
      !esSeccionAutomatica(tipoCodigo, nodo.attrs.clave ?? null)
    ) {
      vacias.push(pos);
    }
  });
  // De atrás hacia adelante: insertar corre las posiciones que siguen, no las anteriores.
  for (const pos of vacias.reverse()) {
    const parrafo = doc.type.schema.nodes.paragraph;
    if (!parrafo) continue;
    tr.insert(pos + 1, parrafo.create({ id: nuevoIdDeBloque() }));
    cambio = true;
  }
  return cambio ? tr : null;
}

/** Las extensiones del editor para un tipo de documento. */
export function extensionesPara(tipoCodigo: string) {
  return [
    Documento,
    Seccion,
    Paragraph,
    Text,
    Bold,
    Italic,
    Underline,
    Heading.configure({ levels: [2, 3] }),
    BulletList,
    ListItem.extend({ content: "paragraph+" }),
    // La variante "firmas" (líneas de firma sin cajas) se guarda solo cuando está puesta:
    // `contenidoDesdeEditor` quita el `null`.
    Table.extend({
      addAttributes() {
        return { ...this.parent?.(), variante: { default: null } };
      },
    }).configure({ resizable: false }),
    TableRow,
    TableHeader.extend({ content: "paragraph+" }),
    TableCell.extend({ content: "paragraph+" }),
    Pasos,
    Paso,
    Nota,
    Imagen,
    Flujograma,
    Referencia,
    ListaDeVerificacion,
    BloqueId.configure({ tipoCodigo }),
    // Fase 4: las marcas del revisor como decoraciones (no son contenido).
    MarcasDeRevision,
    UndoRedo,
    Dropcursor,
    Gapcursor,
  ];
}

/**
 * Lo que sale del editor, validado con el mismo esquema que la API. No debería fallar nunca
 * —el esquema de ProseMirror es un espejo del de Zod—; si falla, es un bug que hay que ver, no
 * algo que se guarde a medias.
 */
export function contenidoDesdeEditor(
  json: JSONContent,
): { ok: true; contenido: Contenido } | { ok: false; problema: string } {
  // Vertical y letra normal es lo de siempre: sin `attrs`, para que abrir y guardar un documento
  // de antes no le cambie el hash. Y solo las marcas puestas: un `densidad: null` al lado de la
  // orientación también cambiaría el hash de los documentos apaisados de antes.
  const { attrs, ...resto } = sinVariantesVacias(json);
  const marcas = Object.fromEntries(
    Object.entries(attrs ?? {}).filter(([, v]) => v !== null && v !== undefined),
  );
  const r = validarContenido(Object.keys(marcas).length ? { ...resto, attrs: marcas } : resto);
  if (r.ok) return r;
  const p = r.problemas[0];
  return { ok: false, problema: p ? `${p.ruta}: ${p.mensaje}` : "contenido inválido" };
}

/**
 * Una tabla sin variante sale sin el atributo: TipTap pone `variante: null` en todas y eso
 * cambiaría el hash de los documentos de antes al guardarlos.
 */
function sinVariantesVacias(nodo: JSONContent): JSONContent {
  const limpio = nodo.content ? { ...nodo, content: nodo.content.map(sinVariantesVacias) } : nodo;
  if (limpio.type === "table" && limpio.attrs && limpio.attrs.variante == null) {
    const { variante: _, ...attrs } = limpio.attrs;
    return { ...limpio, attrs };
  }
  return limpio;
}

/** Lo que el editor inserta con "+ Pasos", "+ Nota"… Sin ids: el plugin los pone. */
export const BLOQUES_NUEVOS: Record<
  "pasos" | "paso" | "nota" | "flujograma" | "listaDeVerificacion",
  JSONContent
> = {
  listaDeVerificacion: {
    type: "listaDeVerificacion",
    attrs: {
      areas: [{ nombre: "Ingreso a la planta", puntos: ["Pisos", "Paredes", "Lámparas"] }],
    },
  },
  pasos: {
    type: "pasos",
    content: [{ type: "paso", attrs: { responsable: "" }, content: [{ type: "paragraph" }] }],
  },
  paso: { type: "paso", attrs: { responsable: "" }, content: [{ type: "paragraph" }] },
  nota: { type: "nota", attrs: { tono: "nota" }, content: [{ type: "paragraph" }] },
  flujograma: {
    type: "flujograma",
    attrs: {
      codigo:
        "flowchart TD\n  A[Inicio] --> B{¿Conforme?}\n  B -- Sí --> C[Fin]\n  B -- No --> D[Corregir] --> B",
    },
  },
};

export function referenciaNueva(codigo: string): JSONContent | null {
  const limpio = codigo.trim().toUpperCase();
  return esCodigoValido(limpio) ? { type: "referencia", attrs: { codigo: limpio } } : null;
}
